import React, { Dispatch, SetStateAction, useContext, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, DialogActions, DialogContent, DialogTitle, Box, Grid } from '@mui/material';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { FormTextField } from '../../../../component/form/FormTextField';
import { EnhancedDialog } from '../../../../component/EnhancedDialog';
import {
  CreateFeatureClassificationsRequest,
  FeatureClassificationView,
  UpdateFeatureClassificationRequest,
} from '../../../../generated/ApiClient';
import { FormDropdown } from '../../../../component/form/FormDropdown';
import RestHttpClient from '../../../../common/RestHttpClient';
import { useQuery } from 'react-query';
import { FormCheckbox } from '../../../../component/form/FormCheckbox';
import { FormNumberInput } from '../../../../component/form/FormNumberInput';
import { ClassificationContext } from '../../context/ClassificationContext.types';

interface GroupFeatureDialogProps {
  isOpen: boolean;
  setOpen: Dispatch<SetStateAction<boolean>>;
  featureClassification: FeatureClassificationView | null;
  onClose: () => void;
  groupId: string;
}

const GroupFeatureDialog = ({
  isOpen,
  setOpen,
  featureClassification,
  onClose,
  groupId,
}: GroupFeatureDialogProps) => {
  const { t } = useTranslation();

  const isEdit = !!featureClassification;

  const { refetchFeatures } = useContext(ClassificationContext);

  const { data: features = [] } = useQuery({
    queryKey: ['classification', 'feature', 'definitions'],
    queryFn: () => RestHttpClient.getFeatureDefinitions(),
    select: (x) => x.data,
  });

  const schema = z.object({
    featureId: z.string(),
    isOverrideable: z.boolean(),
    defaultValue: z.string(),
    sortOrder: z.number(),
  });

  type FormValues = z.infer<typeof schema>;

  const {
    control,
    handleSubmit,
    formState: { isSubmitting },
    reset,
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: { isOverrideable: false, sortOrder: 1 },
  });

  useEffect(() => {
    if (featureClassification)
      reset({
        featureId: featureClassification.definitionId,
        defaultValue: featureClassification.defaultValue,
        isOverrideable: featureClassification.isOverrideable,
        sortOrder: featureClassification.sortOrder,
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [featureClassification]);

  const handleClose = () => {
    setOpen(false);
    onClose();
  };

  const onFormSubmit = async (data: FormValues) => {
    try {
      if (isEdit) {
        await RestHttpClient.updateFeatureClassification(
          new UpdateFeatureClassificationRequest({
            featureDefinitionId: data.featureId,
            classificationGroupId: groupId,
            defaultValue: data.defaultValue,
            isOverrideable: data.isOverrideable,
            sortOrder: data.sortOrder,
          })
        );
      } else {
        await RestHttpClient.createFeatureClassification(
          new CreateFeatureClassificationsRequest({
            featureDefinitionId: data.featureId,
            classificationGroupId: groupId,
            defaultValue: data.defaultValue,
            isOverrideable: data.isOverrideable,
            sortOrder: data.sortOrder,
          })
        );
      }
      await refetchFeatures();
      handleClose();
    } catch (error) {
      console.error('Error saving feature:', error);
    }
  };

  const featureOptions = features.map((x) => ({ label: x.name, value: x.id }));

  return (
    <EnhancedDialog isDialogOpened={isOpen} setIsDialogOpened={setOpen}>
      <DialogTitle>
        {isEdit
          ? t('classification.groups.features.editFeature')
          : t('classification.groups.features.addFeature')}
      </DialogTitle>
      <DialogContent dividers>
        <form id="feature-form" onSubmit={handleSubmit(onFormSubmit)} noValidate>
          <Grid container spacing={3} sx={{ paddingY: 2 }}>
            <Grid item xs={12}>
              <FormDropdown
                control={control}
                name="featureId"
                label={t('classification.groups.features.feature')}
                disabled={isSubmitting}
                shrink
                options={featureOptions}
              />
            </Grid>
            <Grid item xs={12}>
              <FormCheckbox
                control={control}
                name="isOverrideable"
                label={t('classification.groups.features.isOverrideable')}
                disabled={isSubmitting}
              />
            </Grid>
            <Grid item xs={12}>
              <FormTextField
                control={control}
                name="defaultValue"
                label={t('classification.groups.features.defaultValue')}
                disabled={isSubmitting}
                shrink
              />
            </Grid>
            <Grid item xs={12}>
              <FormNumberInput
                control={control}
                name="sortOrder"
                label={t('classification.groups.features.sortOrder')}
                disabled={isSubmitting}
                shrink
              />
            </Grid>
          </Grid>
        </form>
      </DialogContent>
      <DialogActions>
        <Box sx={{ textAlign: 'right' }}>
          <Button
            onClick={handleClose}
            sx={{ border: 1, borderColor: 'black', color: 'black', marginRight: '4px' }}
            disabled={isSubmitting}
          >
            {t('cancel')}
          </Button>
          <Button type="submit" form="feature-form" variant="contained" disabled={isSubmitting}>
            {t('save')}
          </Button>
        </Box>
      </DialogActions>
    </EnhancedDialog>
  );
};

export default GroupFeatureDialog;
